import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import {
  AcceptRequest, ActionRequest, ConsolidatedBalanceSheet, ConsolidatedIncomeStatement, IcMap, IcMapRequest, IcTxn,
  InitiateRequest, Reconciliation, SuggestedAccountsResult,
} from '../interfaces/intercompany.interface';

const E = ENDPOINTS.INTERCOMPANY;
const G = ENDPOINTS.GROUP_CONSOLIDATION;
export type IcTxnAction = 'accept' | 'reject' | 'reverse' | 'withdraw';

async function download(url: string, params: Record<string, string>, fileName: string): Promise<void> {
  const res = await axios.get(url, { params, responseType: 'blob' });
  const href = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement('a');
  link.href = href;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
}

export const IntercompanyService = {
  listTxns: async (institutionId: string, status?: string): Promise<IcTxn[]> =>
    (await axios.get(E.TXNS(institutionId), { params: status ? { status } : {} })).data,
  getTxn: async (id: string): Promise<IcTxn> => (await axios.get(E.TXN(id))).data,
  initiate: async (body: InitiateRequest): Promise<IcTxn> => (await axios.post(E.TXN_BASE, body)).data,
  accept: async (id: string, body?: AcceptRequest): Promise<IcTxn> => (await axios.post(E.TXN_ACTION(id, 'accept'), body ?? {})).data,
  reject: async (id: string, body: ActionRequest): Promise<IcTxn> => (await axios.post(E.TXN_ACTION(id, 'reject'), body)).data,
  withdraw: async (id: string): Promise<IcTxn> => (await axios.post(E.TXN_ACTION(id, 'withdraw'))).data,
  reverse: async (id: string, body: ActionRequest): Promise<IcTxn> => (await axios.post(E.TXN_ACTION(id, 'reverse'), body)).data,
  listMaps: async (institutionId: string): Promise<IcMap[]> => (await axios.get(E.MAPS(institutionId))).data,
  saveMap: async (body: IcMapRequest): Promise<IcMap> => (await axios.put(E.MAP_SAVE, body)).data,
  createSuggestedAccounts: async (institutionId: string): Promise<SuggestedAccountsResult> =>
    (await axios.post(E.SUGGESTED_ACCOUNTS(institutionId))).data,
  reconciliation: async (institutionId: string, asOf?: string): Promise<Reconciliation> =>
    (await axios.get(E.RECONCILIATION(institutionId), { params: asOf ? { asOf } : {} })).data,
};

export const GroupConsolidationService = {
  balanceSheet: async (institutionId: string, asOf: string): Promise<ConsolidatedBalanceSheet> =>
    (await axios.get(G.BALANCE_SHEET(institutionId), { params: { asOf } })).data,
  incomeStatement: async (institutionId: string, from: string, to: string): Promise<ConsolidatedIncomeStatement> =>
    (await axios.get(G.INCOME_STATEMENT(institutionId), { params: { from, to } })).data,
  exportBalanceSheet: (institutionId: string, asOf: string, format: 'pdf' | 'xlsx') =>
    download(G.BALANCE_SHEET_EXPORT(institutionId), { asOf, format }, `group-balance-sheet-${asOf}.${format}`),
  exportIncomeStatement: (institutionId: string, from: string, to: string, format: 'pdf' | 'xlsx') =>
    download(G.INCOME_STATEMENT_EXPORT(institutionId), { from, to, format }, `group-income-statement-${to}.${format}`),
};
